import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSoftKeyboard } from '../../contexts/SoftKeyboardContext';

const numericLayout = [['1', '2', '3'], ['4', '5', '6'], ['7', '8', '9'], ['.', '0', '⌫']];
/** Letters for names – compact */
const alphabeticLayout = [
  ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
  ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
  ['z', 'x', 'c', 'v', 'b', 'n', 'm', '⌫'],
];
/** Special characters # ! @ $ % ^ & * ( ) _ + < > ? " : ; / . ' ] } etc */
const symbolsLayout = [
  ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
  ['!', '@', '#', '$', '%', '^', '&', '*', '(', ')'],
  ['-', '_', '+', '=', '[', ']', '{', '}', '|', '\\'],
  [';', ':', "'", '"', ',', '.', '<', '>', '/', '?'],
];

type DisplayMode = 'numeric' | 'alphabetic' | 'symbols';

const SoftKeyboardOverlay: React.FC = () => {
  const { activeRequest, closeKeyboard } = useSoftKeyboard();
  const [buffer, setBuffer] = useState('');
  const [isCaps, setIsCaps] = useState(false);
  /** User can switch between numeric and alphabetic regardless of field type */
  const [displayMode, setDisplayMode] = useState<DisplayMode>('alphabetic');

  useEffect(() => {
    if (activeRequest) {
      const newValue = activeRequest.value || '';
      setBuffer(newValue);
      setIsCaps(false);
      // Initial display mode from field type
      setDisplayMode(activeRequest.type === 'numeric' ? 'numeric' : 'alphabetic');
    } else {
      setBuffer('');
    }
  }, [activeRequest?.id, activeRequest?.value, activeRequest?.type]);

  const handleKeyPress = useCallback((key: string) => {
    if (!activeRequest) return;
    let next: string;

    if (key === '⌫') {
      next = buffer.slice(0, -1);
    } else if (key === 'CLR') {
      next = '';
    } else if (displayMode === 'numeric' && key === '.' && buffer.includes('.')) {
      return;
    } else {
      const normalized = isCaps && displayMode === 'alphabetic' && key.length === 1 && /[a-z]/.test(key) ? key.toUpperCase() : key;
      next = `${buffer}${normalized}`;
    }

    setBuffer(next);
    activeRequest.onChange(next);
  }, [activeRequest, buffer, isCaps, displayMode]);

  const handleDone = useCallback(() => {
    if (!activeRequest) return;
    
    // Store references to avoid stale closures
    const request = activeRequest;
    const finalBuffer = buffer;
    
    // CRITICAL: Always ensure the final buffer value is saved to the onChange handler
    // This must happen synchronously before anything else
    // The onChange will update the React state (use native setter in useAutoSoftKeyboard for controlled inputs)
    request.onChange(finalBuffer);
    
    // Defer blur/submit so React can process the onChange state update first (avoids clearing on Done)
    const doSubmit = () => {
      if (request.onSubmit) {
        try {
          request.onSubmit();
        } catch (error) {
          console.error('Error in onSubmit handler:', error);
        }
      } else {
        const activeElement = document.activeElement as HTMLInputElement | HTMLTextAreaElement;
        if (activeElement) {
          if ((activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA') && activeElement.form) {
            const submitButton = activeElement.form.querySelector('button[type="submit"], input[type="submit"]') as HTMLButtonElement | HTMLInputElement;
            if (submitButton && !submitButton.disabled) {
              activeElement.form.requestSubmit();
            } else {
              activeElement.blur();
            }
          } else {
            activeElement.blur();
          }
        }
      }
      setTimeout(() => closeKeyboard(), 50);
    };
    setTimeout(doSubmit, 0);
  }, [activeRequest, buffer, closeKeyboard]);

  const handleClose = useCallback(() => {
    closeKeyboard();
  }, [closeKeyboard]);

  useEffect(() => {
    if (!activeRequest) return;
    const handlePhysicalKey = (event: KeyboardEvent) => {
      if (!activeRequest) return;
      if (event.key === 'Tab' || event.key === 'Shift' || event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }

      if (event.key === 'Backspace') {
        event.preventDefault();
        handleKeyPress('⌫');
        return;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        handleDone();
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        handleClose();
        return;
      }

      if (displayMode === 'numeric') {
        if (/^[0-9]$/.test(event.key)) {
          event.preventDefault();
          handleKeyPress(event.key);
          return;
        }
        if (event.key === '.') {
          event.preventDefault();
          handleKeyPress('.');
          return;
        }
        return;
      }

      if (event.key === ' ') {
        event.preventDefault();
        handleKeyPress(' ');
        return;
      }

      if (event.key.length === 1) {
        event.preventDefault();
        handleKeyPress(event.key);
      }
    };

    window.addEventListener('keydown', handlePhysicalKey);
    return () => {
      window.removeEventListener('keydown', handlePhysicalKey);
    };
  }, [activeRequest, displayMode, handleClose, handleDone, handleKeyPress]);

  const layout = useMemo(() => {
    if (!activeRequest) return [] as string[][];
    if (displayMode === 'numeric') return numericLayout;
    if (displayMode === 'symbols') return symbolsLayout;
    return alphabeticLayout;
  }, [activeRequest, displayMode]);

  if (!activeRequest) return null;

  const isNumeric = displayMode === 'numeric';
  const isSymbols = displayMode === 'symbols';
  const isAlphabetic = displayMode === 'alphabetic';

  const switchToNumeric = () => setDisplayMode('numeric');
  const switchToAlphabetic = () => setDisplayMode('alphabetic');
  const switchToSymbols = () => setDisplayMode('symbols');

  // Key sizing: tuned for tablet use (a bit larger, but not huge)
  const keySizeClass = isNumeric ? 'max-w-[64px]' : 'max-w-[40px]';

  /** Compact keyboard – matches Add customer field size; switch between numeric/alphabetic/symbols */
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-end bg-black/40 pointer-events-none">
      <div
        className={`w-full mx-auto mb-3 bg-gray-900 text-white rounded-lg shadow-xl p-3 pointer-events-auto ${
          isNumeric ? 'max-w-[320px]' : 'max-w-lg'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2 mb-2">
          {activeRequest.label && <span className="text-[11px] text-gray-400 truncate flex-1 min-w-0">{activeRequest.label}</span>}
          <div className="flex-1 min-w-0 px-2 py-1.5 bg-gray-800 rounded text-right font-mono text-sm">
            {activeRequest.maskValue ? (buffer ? '*'.repeat(buffer.length) : ' ') : buffer || ' '}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button type="button" onClick={switchToNumeric} className="px-1.5 py-1 rounded text-[10px] bg-gray-700 hover:bg-gray-600">
              123
            </button>
            <button type="button" onClick={switchToAlphabetic} className="px-1.5 py-1 rounded text-[10px] bg-gray-700 hover:bg-gray-600">
              ABC
            </button>
            <button type="button" onClick={switchToSymbols} className="px-1.5 py-1 rounded text-[10px] bg-gray-700 hover:bg-gray-600">
              ?#
            </button>
            {isAlphabetic && (
              <button
                type="button"
                onClick={() => setIsCaps((prev) => !prev)}
                className={`px-1.5 py-1 rounded text-[10px] ${isCaps ? 'bg-pink-500 text-white' : 'bg-gray-700 text-gray-200'}`}
              >
                {isCaps ? 'ABC' : 'abc'}
              </button>
            )}
            <button type="button" onClick={handleClose} className="px-1.5 py-1 rounded text-[10px] bg-gray-700 hover:bg-gray-600">
              Close
            </button>
          </div>
        </div>
          <div className="space-y-1">
          {layout.map((row, rowIndex) => (
            <div key={`row-${rowIndex}`} className="flex justify-center gap-0.5">
              {row.map((key) => (
                <button
                  key={`${rowIndex}-${key}`}
                  type="button"
                  onClick={() => handleKeyPress(key)}
                  className={`flex-1 ${keySizeClass} py-2.5 rounded text-base font-semibold bg-gray-800 hover:bg-gray-700 active:bg-pink-600`}
                >
                  {isAlphabetic && isCaps && key.length === 1 && /[a-z]/.test(key) ? key.toUpperCase() : key}
                </button>
              ))}
            </div>
          ))}
          <div className="flex justify-center gap-0.5 mt-1">
            {isNumeric ? (
              <>
                <div className="flex-[3] max-w-[48px]" />
                <button type="button" onClick={() => handleKeyPress('CLR')} className="flex-1 max-w-[64px] py-2.5 rounded text-base font-semibold bg-gray-700 hover:bg-gray-600">
                  CLR
                </button>
                <button type="button" onClick={handleDone} className="flex-1 max-w-[64px] py-2.5 rounded text-base font-semibold bg-pink-600 hover:bg-pink-500">
                  Done
                </button>
              </>
            ) : (
              <>
                <button type="button" onClick={() => handleKeyPress('⌫')} className="flex-1 max-w-[44px] py-2 rounded text-sm font-semibold bg-gray-700 hover:bg-gray-600">
                  ⌫
                </button>
                <button type="button" onClick={() => handleKeyPress(' ')} className="flex-[2] max-w-[80px] py-2 rounded text-sm font-semibold bg-gray-700 hover:bg-gray-600">
                  Space
                </button>
                <button type="button" onClick={() => handleKeyPress('CLR')} className="flex-1 max-w-[44px] py-2 rounded text-sm font-semibold bg-gray-700 hover:bg-gray-600">
                  CLR
                </button>
                <button type="button" onClick={handleDone} className="flex-1 max-w-[44px] py-2 rounded text-sm font-semibold bg-pink-600 hover:bg-pink-500">
                  Done
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SoftKeyboardOverlay;
